import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Hero.css';

const SLIDE_IDS = ['brand', 'store', 'services'];
const ROTATE_MS = 7000;

/** Rutas finales tras generar arte IA (linocut); placeholders copiados del repo hasta sustituir. */
const CAROUSEL_BG_IMAGES = {
  brand: '/images/hero-carousel-marca-ecosistema.png',
  store: '/images/hero-carousel-tienda-proposito.png'
};

function HeroTriangleXRaySvg() {
  return (
    <div className="hero-slide-xray-overlay" aria-hidden="true">
      <svg className="hero-slide-xray-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
        <polygon
          className="hero-slide-xray-triangle"
          points="200,80 320,320 80,320"
          fill="none"
          stroke="var(--javiland-hueso)"
          strokeWidth="1.5"
        />
        <g className="hero-slide-xray-rays">
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30) * (Math.PI / 180);
            const x1 = 200 + Math.cos(angle) * 60;
            const y1 = 200 + Math.sin(angle) * 60;
            const x2 = 200 + Math.cos(angle) * 220;
            const y2 = 200 + Math.sin(angle) * 220;
            return (
              <line
                key={`ray-${String(i)}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="var(--javiland-hueso)"
                strokeWidth="0.8"
              />
            );
          })}
        </g>
        <g className="hero-slide-xray-diagonals">
          <line x1="80" y1="320" x2="200" y2="80" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
          <line x1="320" y1="320" x2="200" y2="80" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
          <line x1="80" y1="320" x2="320" y2="320" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
        </g>
      </svg>
    </div>
  );
}

const Hero = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const go = useCallback((dir) => {
    setActive((i) => {
      const n = SLIDE_IDS.length;
      return (i + dir + n) % n;
    });
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % SLIDE_IDS.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  /* 1 marca → triángulo | 2 tienda → foto tienda | 3 experiencias → foto marca (ecosistema) */
  const slideMeta = [
    { variant: 'triangle', path: '/', num: 1 },
    { variant: 'photo', imageSrc: CAROUSEL_BG_IMAGES.store, path: '/tienda', num: 2 },
    { variant: 'photo', imageSrc: CAROUSEL_BG_IMAGES.brand, path: '/servicios', num: 3 }
  ];

  return (
    <section className="hero hero-fou" id="inicio">
      <div className="hero-banners">
        <div
          className="hero-banner hero-banner-image hero-banner-community"
          onClick={() => navigateToPage('/servicios')}
          role="presentation"
        >
          <img
            src="/images/hero-viajes-colombia.png"
            alt="4 viajes de ecoturismo comunitario: Pacífico, San Andrés, Amazonas y Tayrona"
            fetchPriority="high"
            loading="eager"
          />
          <div className="banner-overlay">
            <h3>{t('hero.communityWellness')}</h3>
            <button
              type="button"
              className="banner-cta"
              onClick={(e) => {
                e.stopPropagation();
                navigateToPage('/servicios');
              }}
            >
              {t('hero.viewServices')}
            </button>
          </div>
        </div>

        <div
          className="hero-carousel"
          role="region"
          aria-roledescription={t('hero.carousel.ariaCarousel')}
          aria-label={t('hero.carousel.ariaLabel')}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
          }}
        >
          {slideMeta.map((slide, index) => (
            <div
              key={SLIDE_IDS[index]}
              className={`hero-carousel-slide ${active === index ? 'is-active' : ''} ${slide.variant === 'photo' ? 'hero-carousel-slide--photo' : ''} ${slide.variant === 'triangle' ? 'hero-carousel-slide--triangle' : ''}`}
              aria-hidden={active !== index}
            >
              <div
                className={`hero-slide-bg ${slide.variant === 'photo' ? 'hero-slide-bg--photo' : 'hero-slide-bg--triangle'}`}
                style={
                  slide.variant === 'photo'
                    ? { backgroundImage: `url(${slide.imageSrc})` }
                    : undefined
                }
                aria-hidden="true"
              >
                {slide.variant === 'triangle' && <HeroTriangleXRaySvg />}
              </div>
              <div className="hero-carousel-content">
                <h2 className="hero-carousel-title">{t(`hero.carousel.slide${slide.num}.title`)}</h2>
                <p className="hero-carousel-subtitle">{t(`hero.carousel.slide${slide.num}.subtitle`)}</p>
                <a
                  href={slide.path}
                  className="hero-carousel-cta"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateToPage(slide.path);
                  }}
                >
                  {t(`hero.carousel.slide${slide.num}.cta`)}
                </a>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="hero-carousel-control hero-carousel-prev"
            aria-label={t('hero.carousel.prev')}
            onClick={() => go(-1)}
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="hero-carousel-control hero-carousel-next"
            aria-label={t('hero.carousel.next')}
            onClick={() => go(1)}
          >
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
          <div className="hero-carousel-indicators" role="tablist" aria-label={t('hero.carousel.dotsLabel')}>
            {SLIDE_IDS.map((_, i) => (
              <button
                key={SLIDE_IDS[i]}
                type="button"
                role="tab"
                aria-selected={active === i}
                aria-label={t('hero.carousel.goTo', { n: i + 1 })}
                className={`hero-carousel-indicator ${active === i ? 'is-active' : ''}`}
                onClick={() => setActive(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
